/**
 * PasswordChangeSection - секция «Изменение пароля».
 *
 * Смена пароля через /api/v2/profile/password: пользователь берется из сессии, логин не передается.
 * Требования повторяют серверные (backend/services/profileView.js, passwordError):
 * не короче 6 символов, без русских букв и пробелов по краям, не совпадает с логином.
 * Индикатор надежности - подсказка, на прием пароля не влияет.
 */
import React, { useState } from 'react';
import { Box, Paper, TextField, Button, IconButton, InputAdornment, Typography, CircularProgress } from '@mui/material';
import { KeyOutlined, Visibility, VisibilityOff, CheckCircle, RadioButtonUnchecked, Cancel } from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { changePassword } from '../profileApi';

export interface PasswordChangeSectionProps {
  username: string;
  /** Дата последней смены пароля «YYYY-MM-DD HH:MM:SS» */
  passwordUpdatedAt: string | null;
  /** Перечитать профиль после смены пароля */
  onChanged: () => Promise<void>;
}

const MIN_LENGTH = 6;
const MAX_LENGTH = 128;

// Раскладка - по ширине колонки разделов и формы, а не окна: рядом с боковым меню профиля они заметно уже окна.
// Таблица требований встает справа от формы, когда обеим хватает места; новый пароль и подтверждение - в ряд
// только в широкой форме, иначе подписи полей обрезаются
const SECTION_WIDE = '@container (min-width: 760px)';
const FIELDS_WIDE = '@container (min-width: 520px)';
const CYRILLIC = /[а-яё]/i;
const WRONG_CURRENT = 'Текущий пароль указан неверно';
const COMMON_PASSWORDS = ['123456', '1234567', '12345678', '123456789', 'qwerty', 'qwerty123', 'password', '111111', '000000', 'zerde', 'zerde123', 'admin', 'apteka'];

interface Rule {
  label: string;
  ok: boolean;
}

/** Надежность 0..4: длина, разные классы символов; простые и повторяющиеся пароли - 0 */
const strengthOf = (password: string): number => {
  if (password.length < MIN_LENGTH) return 0;
  const lower = password.toLowerCase();
  if (COMMON_PASSWORDS.indexOf(lower) >= 0 || /^(.)\1+$/.test(password) || '01234567890123456789'.indexOf(password) >= 0) return 0;
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/\d/.test(password)) classes += 1;
  if (/[^A-Za-z0-9]/.test(password)) classes += 1;
  let score = classes - 1;
  if (password.length >= 10) score += 1;
  if (password.length >= 14) score += 1;
  return Math.max(0, Math.min(4, score));
};

const STRENGTH = [
  { label: 'Очень слабый', color: ZERDE_COLORS.ruby },
  { label: 'Слабый', color: ZERDE_COLORS.orange },
  { label: 'Средний', color: ZERDE_COLORS.amber },
  { label: 'Надежный', color: ZERDE_COLORS.emerald },
  { label: 'Очень надежный', color: '#15803d' }
];

/** «2026-03-12 14:05:00» -> «12.03.2026 14:05» и число дней с тех пор */
const describeUpdatedAt = (value: string | null): { text: string; days: number | null } => {
  const match = value ? value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/) : null;
  if (!match) return { text: 'нет данных', days: null };
  const [, y, m, d, hh, mm] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
  return { text: `${d}.${m}.${y} ${hh}:${mm}`, days };
};

const daysText = (days: number): string => {
  if (days === 0) return 'сегодня';
  const mod10 = days % 10;
  const mod100 = days % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'день' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'дня' : 'дней';
  return `${days} ${word} назад`;
};

const PasswordChangeSection: React.FC<PasswordChangeSectionProps> = ({ username, passwordUpdatedAt, onChanged }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visible, setVisible] = useState<Record<'current' | 'next' | 'confirm', boolean>>({ current: false, next: false, confirm: false });
  const [capsLock, setCapsLock] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [changing, setChanging] = useState(false);

  const rules: Rule[] = [
    { label: `Не менее ${MIN_LENGTH} символов`, ok: newPassword.length >= MIN_LENGTH && newPassword.length <= MAX_LENGTH },
    { label: 'Без русских букв', ok: newPassword.length > 0 && !CYRILLIC.test(newPassword) },
    { label: 'Без пробелов в начале и в конце', ok: newPassword.length > 0 && newPassword.trim() === newPassword },
    { label: 'Не совпадает с логином', ok: newPassword.length > 0 && newPassword.toLowerCase() !== username.trim().toLowerCase() },
    { label: 'Отличается от текущего', ok: newPassword.length > 0 && newPassword !== currentPassword }
  ];
  const rulesOk = rules.every(rule => rule.ok);
  const rulesDone = rules.filter(rule => rule.ok).length;
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const strength = strengthOf(newPassword);
  const updated = describeUpdatedAt(passwordUpdatedAt);

  const trackCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock'));
  };

  const toggle = (key: 'current' | 'next' | 'confirm') => setVisible(prev => ({ ...prev, [key]: !prev[key] }));

  const visibilityAdornment = (key: 'current' | 'next' | 'confirm') => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={() => toggle(key)} aria-label={visible[key] ? 'Скрыть пароль' : 'Показать пароль'}>
        {visible[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!currentPassword) {
      setCurrentError('Введите текущий пароль');
      return;
    }
    if (!rulesOk) {
      notify.warning('Новый пароль не отвечает требованиям');
      return;
    }
    if (newPassword !== confirmPassword) {
      notify.warning('Новый пароль и подтверждение не совпадают');
      return;
    }

    setChanging(true);
    try {
      const result = await changePassword(currentPassword, newPassword);
      notify.success(result.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitted(false);
      setCurrentError(null);
      await onChanged();
    } catch (err) {
      const message = apiErrorMessage(err, 'Не удалось изменить пароль');
      if (message === WRONG_CURRENT) setCurrentError(message);
      notify.error(message);
    } finally {
      setChanging(false);
    }
  };

  return (
    <Box
      id="section-password"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 3,
        scrollMarginTop: '24px',
        [SECTION_WIDE]: { flexDirection: 'row' }
      }}
    >
      {/* Левая карточка: Форма изменения пароля */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: '20px',
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          flex: '1 1 0',
          minWidth: 0,
          containerType: 'inline-size'
        }}
      >
        <SectionHeaderDivider
          id="header-password"
          icon={<KeyOutlined sx={{ fontSize: 22 }} />}
          title="Изменение пароля"
          subtitle="После смены пароля сессии на других устройствах завершаются"
          badge={updated.days !== null && (
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
              Изменен {updated.text} · {daysText(updated.days)}
            </Typography>
          )}
        />

        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          {/* Скрытый логин помогает менеджеру паролей браузера сохранить новый пароль для нужной учетной записи */}
          <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Текущий пароль"
              type={visible.current ? 'text' : 'password'}
              fullWidth
              size="small"
              value={currentPassword}
              onChange={e => {
                setCurrentPassword(e.target.value);
                setCurrentError(null);
              }}
              error={Boolean(currentError)}
              helperText={currentError || undefined}
              FormHelperTextProps={{ sx: { display: currentError ? 'block' : 'none' } }}
              inputProps={{ autoComplete: 'current-password', onKeyUp: trackCapsLock, onKeyDown: trackCapsLock }}
              InputProps={{ endAdornment: visibilityAdornment('current') }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', alignItems: 'start', gap: 2, [FIELDS_WIDE]: { gridTemplateColumns: '1fr 1fr' } }}>
              <Box sx={{ minWidth: 0 }}>
                <TextField
                  label="Новый пароль"
                  type={visible.next ? 'text' : 'password'}
                  fullWidth
                  size="small"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  error={submitted && !rulesOk}
                  inputProps={{ autoComplete: 'new-password', maxLength: MAX_LENGTH, onKeyUp: trackCapsLock, onKeyDown: trackCapsLock }}
                  InputProps={{ endAdornment: visibilityAdornment('next') }}
                />
                <Box
                  sx={{
                    mt: 1,
                    minHeight: 18,
                    display: newPassword.length > 0 ? 'flex' : 'none',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                  aria-label="Надежность пароля"
                >
                  {newPassword.length > 0 && (
                    <>
                      <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                        {STRENGTH.map((level, index) => (
                          <Box
                            key={level.label}
                            sx={{ flex: 1, height: 4, borderRadius: '2px', bgcolor: index <= strength ? STRENGTH[strength].color : '#e2e8f0', transition: 'background-color 0.2s' }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: STRENGTH[strength].color, lineHeight: 1 }}>
                        {STRENGTH[strength].label}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <TextField
                label="Подтверждение нового пароля"
                type={visible.confirm ? 'text' : 'password'}
                fullWidth
                size="small"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                error={confirmMismatch || (submitted && !confirmPassword)}
                helperText={confirmMismatch ? 'Пароли не совпадают' : undefined}
                FormHelperTextProps={{ sx: { display: confirmMismatch ? 'block' : 'none' } }}
                inputProps={{ autoComplete: 'new-password', maxLength: MAX_LENGTH, onKeyUp: trackCapsLock, onKeyDown: trackCapsLock }}
                InputProps={{ endAdornment: visibilityAdornment('confirm') }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={changing}
              startIcon={changing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                bgcolor: ZERDE_COLORS.primary,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              {changing ? 'Сохранение...' : 'Обновить пароль'}
            </Button>
            {capsLock && (
              <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 600 }}>
                Включен Caps Lock
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Таблица требований справа от Paper: оригинальный дизайн */}
      <Box
        sx={{
          width: '100%',
          [SECTION_WIDE]: { width: 300 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: '#ffffff'
        }}
      >
        <Box sx={{ px: 2, py: 1.25, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Требования к паролю
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: rulesOk ? ZERDE_COLORS.emerald : '#64748b', whiteSpace: 'nowrap' }}>
            {rulesDone} из {rules.length}
          </Typography>
        </Box>

        <Box component="ul" aria-label="Требования к паролю" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {rules.map((rule, index) => {
            const failed = !rule.ok && (submitted || newPassword.length > 0);
            const Icon = rule.ok ? CheckCircle : failed ? Cancel : RadioButtonUnchecked;
            const color = rule.ok ? ZERDE_COLORS.emerald : failed ? ZERDE_COLORS.ruby : '#cbd5e1';
            return (
              <Box
                component="li"
                key={rule.label}
                sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.25, borderTop: index > 0 ? '1px solid #f1f5f9' : 'none' }}
              >
                <Icon sx={{ fontSize: 16, color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: rule.ok ? '#1e293b' : '#64748b' }}>
                  {rule.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" sx={{ mt: 'auto', px: 2, py: 1.25, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', color: '#94a3b8', lineHeight: 1.5 }}>
          Надежнее всего 10+ символов: буквы разного регистра, цифры и знаки
        </Typography>
      </Box>
    </Box>
  );
};

export default PasswordChangeSection;
